import type { CSSProperties } from "react"

import { Fragment, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import dynamic from "next/dynamic"
import { gsap } from "gsap"
import { Moon, Sun } from "lucide-react"

import type { Mode } from "./hardback-data"

import {
    BG_IMAGE_DARK,
    BG_IMAGE_LIGHT,
    BOOKS,
    CLOSE_DURATION,
    DRAG_THRESHOLD_PX,
    ENTRANCE_DURATION,
    INITIAL_INDEX,
    OPEN_DURATION,
    SETTLE_IDLE_MS,
    WHEEL_PX_PER_SLIDE,
    clamp,
    modN
} from "./hardback-data"

const HardbackScene = dynamic(() => import("./hardback-scene"), { ssr: false })

const useIsoLayout = typeof window !== "undefined" ? useLayoutEffect : useEffect

const GRAIN = `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='160' height='160'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`

const SERIF_VAR = 'var(--font-hardback-serif), "Cormorant Garamond", Georgia, serif'

function renderFlipTitle(text: string, opts: { italic: boolean; baseKey: string }) {
    return Array.from(text).map((char, i) => (
        <span
            key={`${opts.baseKey}-${i}`}
            className="title-letter inline-block"
            style={{
                transformOrigin: "50% 100%",
                transformStyle: "preserve-3d",
                backfaceVisibility: "hidden",
                willChange: "transform, opacity",
                fontStyle: opts.italic ? "italic" : "normal",
                fontWeight: opts.italic ? 400 : 500,
                whiteSpace: "pre"
            }}
        >
            {char === " " ? " " : char}
        </span>
    ))
}

export function Hardback() {
    // ---- Theme ---------------------------------------------------------------
    const [isDark, setIsDark] = useState(true)

    useEffect(() => {
        const readFromDOM = () => {
            const t = document.documentElement.getAttribute("data-theme")
            setIsDark(t !== "light")
        }
        try {
            const saved = localStorage.getItem("hardback-theme")
            if (saved === "light" || saved === "dark") {
                document.documentElement.setAttribute("data-theme", saved)
            }
        } catch {
            // ignore storage failures
        }
        readFromDOM()

        const onEvent = () => readFromDOM()
        window.addEventListener("theme-change", onEvent)
        const observer = new MutationObserver(readFromDOM)
        observer.observe(document.documentElement, {
            attributes: true,
            attributeFilter: ["data-theme"]
        })
        return () => {
            window.removeEventListener("theme-change", onEvent)
            observer.disconnect()
        }
    }, [])

    const toggleTheme = useCallback(() => {
        const next = isDark ? "light" : "dark"
        document.documentElement.setAttribute("data-theme", next)
        try {
            localStorage.setItem("hardback-theme", next)
        } catch {
            // ignore storage failures
        }
        window.dispatchEvent(new Event("theme-change"))
    }, [isDark])

    // ---- Responsive ----------------------------------------------------------
    const [isMobile, setIsMobile] = useState(false)
    useEffect(() => {
        const check = () => setIsMobile(window.innerWidth < 768)
        check()
        window.addEventListener("resize", check)
        return () => window.removeEventListener("resize", check)
    }, [])

    // ---- State machine -------------------------------------------------------
    const [mode, setMode] = useState<Mode>("entering")
    const modeRef = useRef<Mode>("entering")
    useEffect(() => {
        modeRef.current = mode
    }, [mode])

    const [activeIndex, setActiveIndex] = useState(INITIAL_INDEX)
    const [selectedIndex, setSelectedIndex] = useState(-1)
    const [showPanel, setShowPanel] = useState(false)

    // ---- Shared per-frame refs ----------------------------------------------
    const positionRef = useRef(INITIAL_INDEX)
    const targetRef = useRef(INITIAL_INDEX)
    const openProgressRef = useRef(0)
    const enterProgressRef = useRef(0)
    const selectedIndexRef = useRef(-1)
    const hoverIndexRef = useRef(-1)
    const panelRevealTweenRef = useRef<gsap.core.Tween | null>(null)

    // ---- DOM refs ------------------------------------------------------------
    const sectionRef = useRef<HTMLElement>(null)
    const titleRef = useRef<HTMLDivElement>(null)
    const subtitleRef = useRef<HTMLDivElement>(null)
    const activeLineRef = useRef<HTMLDivElement>(null)
    const buyPanelRef = useRef<HTMLElement>(null)
    const glowRef = useRef<HTMLDivElement>(null)

    // ---- Entrance (starts when the 3D scene is ready) -----------------------
    const entranceStartedRef = useRef(false)
    const entranceTweenRef = useRef<gsap.core.Tween | null>(null)

    const startEntrance = useCallback(() => {
        if (entranceStartedRef.current) return
        entranceStartedRef.current = true
        enterProgressRef.current = 0
        entranceTweenRef.current = gsap.to(enterProgressRef, {
            current: 1,
            duration: ENTRANCE_DURATION,
            ease: "none",
            onComplete: () => setMode("browsing")
        })
    }, [])

    useEffect(() => {
        // Safety net: begin even if the scene never reports ready.
        const t = setTimeout(startEntrance, 2000)
        return () => {
            clearTimeout(t)
            entranceTweenRef.current?.kill()
        }
    }, [startEntrance])

    // ---- Active book polling -------------------------------------------------
    useEffect(() => {
        let last = INITIAL_INDEX
        let raf = 0
        const tick = () => {
            const rounded = Math.round(positionRef.current)
            const idx = modN(rounded, BOOKS.length)
            if (idx !== last) {
                last = idx
                setActiveIndex(idx)
            }
            raf = requestAnimationFrame(tick)
        }
        raf = requestAnimationFrame(tick)
        return () => cancelAnimationFrame(raf)
    }, [])

    // ---- Ambient glow pulse --------------------------------------------------
    useEffect(() => {
        const el = glowRef.current
        if (!el) return
        const tw = gsap.to(el, {
            opacity: 0.5,
            duration: 4.5,
            ease: "sine.inOut",
            repeat: -1,
            yoyo: true
        })
        return () => {
            tw.kill()
        }
    }, [])

    // ---- Title / subtitle reveal --------------------------------------------
    useIsoLayout(() => {
        const el = titleRef.current
        if (!el) return
        const letters = el.querySelectorAll(".title-letter")
        if (!letters.length) return

        const tl = gsap.timeline({ delay: 0.7 })

        tl.fromTo(
            letters,
            { rotateX: -90, z: -45, y: 30, scaleY: 0.42, scaleX: 0.96, opacity: 0, transformOrigin: "50% 100%" },
            {
                rotateX: 0,
                z: 0,
                y: 0,
                scaleY: 1,
                scaleX: 1,
                opacity: 1,
                duration: 1.1,
                ease: "back.out(1.15)",
                stagger: { each: 0.028, from: "center" },
                force3D: true
            }
        )
        tl.to(
            letters,
            { scale: 1.025, duration: 0.16, ease: "sine.out", stagger: { each: 0.012, from: "center" } },
            "-=0.55"
        )
        tl.to(
            letters,
            { scale: 1, duration: 0.32, ease: "sine.inOut", stagger: { each: 0.012, from: "center" } },
            "-=0.08"
        )
        if (subtitleRef.current) {
            tl.fromTo(
                subtitleRef.current,
                { autoAlpha: 0, y: 14 },
                { autoAlpha: 1, y: 0, duration: 0.65, ease: "power3.out" },
                "-=0.4"
            )
        }
        if (activeLineRef.current) {
            tl.fromTo(
                activeLineRef.current,
                { autoAlpha: 0, y: 10, scaleX: 0.9, transformOrigin: "50% 50%" },
                { autoAlpha: 1, y: 0, scaleX: 1, duration: 0.6, ease: "power3.out" },
                "-=0.45"
            )
        }

        return () => {
            tl.kill()
        }
    }, [])

    // ---- Open / close --------------------------------------------------------
    const openBook = useCallback((index: number) => {
        if (modeRef.current !== "browsing") return

        const distance = Math.abs(index - positionRef.current)
        const isCentred = distance < 0.4

        setMode("opening")
        modeRef.current = "opening"
        targetRef.current = index

        gsap.killTweensOf(positionRef)
        gsap.killTweensOf(openProgressRef)
        openProgressRef.current = 0

        const startOpening = () => {
            setSelectedIndex(index)
            selectedIndexRef.current = index

            panelRevealTweenRef.current?.kill()
            panelRevealTweenRef.current = gsap.delayedCall(OPEN_DURATION * 0.72, () => {
                setShowPanel(true)
            })

            gsap.to(openProgressRef, {
                current: 1,
                duration: OPEN_DURATION,
                ease: "power2.inOut",
                onComplete: () => {
                    setMode("open")
                    modeRef.current = "open"
                }
            })
        }

        if (isCentred) {
            positionRef.current = index
            startOpening()
        } else {
            const snapDur = clamp(0.22 + distance * 0.08, 0.32, 0.7)
            gsap.to(positionRef, {
                current: index,
                duration: snapDur,
                ease: "power3.out",
                onComplete: () => {
                    gsap.delayedCall(0.12, startOpening)
                }
            })
        }
    }, [])

    const closeBook = useCallback(() => {
        const m = modeRef.current
        if (m !== "open" && m !== "opening") return

        setMode("closing")
        modeRef.current = "closing"

        panelRevealTweenRef.current?.kill()
        panelRevealTweenRef.current = null
        setShowPanel(false)

        gsap.killTweensOf(openProgressRef)
        gsap.to(openProgressRef, {
            current: 0,
            duration: CLOSE_DURATION,
            ease: "power2.inOut",
            onComplete: () => {
                setSelectedIndex(-1)
                selectedIndexRef.current = -1
                setMode("browsing")
                modeRef.current = "browsing"
            }
        })
    }, [])

    const handleBookClick = useCallback(
        (index: number) => {
            const m = modeRef.current
            if (m === "browsing") openBook(index)
            else if (m === "open") closeBook()
        },
        [openBook, closeBook]
    )

    const handleBookHover = useCallback((index: number) => {
        hoverIndexRef.current = index
        if (sectionRef.current) sectionRef.current.style.cursor = "pointer"
    }, [])

    const handleBookOut = useCallback(() => {
        hoverIndexRef.current = -1
        if (sectionRef.current) sectionRef.current.style.cursor = ""
    }, [])

    // ---- Input (wheel / drag / keyboard) ------------------------------------
    useEffect(() => {
        const section = sectionRef.current
        if (!section) return

        const N = BOOKS.length
        let drag = { active: false, startX: 0, startPosition: 0, pointerId: 0, moved: false }
        let settleTimer: ReturnType<typeof setTimeout> | null = null

        const onWheel = (e: WheelEvent) => {
            if (modeRef.current !== "browsing") return
            e.preventDefault()
            const delta = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY
            const slideDelta = delta / WHEEL_PX_PER_SLIDE
            targetRef.current = clamp(targetRef.current + slideDelta, 0, N - 1)
            if (settleTimer) clearTimeout(settleTimer)
            settleTimer = setTimeout(() => {
                targetRef.current = clamp(Math.round(targetRef.current), 0, N - 1)
            }, SETTLE_IDLE_MS)
        }

        const onPointerDown = (e: PointerEvent) => {
            if (modeRef.current !== "browsing") return
            if (e.pointerType === "mouse" && e.button !== 0) return
            if ((e.target as HTMLElement)?.closest("[data-no-drag]")) return
            drag = {
                active: true,
                startX: e.clientX,
                startPosition: positionRef.current,
                pointerId: e.pointerId,
                moved: false
            }
        }

        const onPointerMove = (e: PointerEvent) => {
            if (!drag.active) return
            const dx = e.clientX - drag.startX
            if (Math.abs(dx) > 4 && !drag.moved) {
                drag.moved = true
                try {
                    section.setPointerCapture(e.pointerId)
                } catch {
                    // capture may fail; ignore
                }
            }
            if (!drag.moved) return
            const slideDelta = -dx / (DRAG_THRESHOLD_PX * 1.4)
            const next = clamp(drag.startPosition + slideDelta, 0, N - 1)
            positionRef.current = next
            targetRef.current = next
        }

        const onPointerUp = (e: PointerEvent) => {
            if (!drag.active) return
            const moved = drag.moved
            drag.active = false
            if (moved) {
                try {
                    section.releasePointerCapture(e.pointerId)
                } catch {
                    // ignore
                }
                targetRef.current = clamp(Math.round(targetRef.current), 0, N - 1)
            }
        }

        const onKey = (e: KeyboardEvent) => {
            if (modeRef.current === "open" && e.key === "Escape") {
                e.preventDefault()
                closeBook()
                return
            }
            if (modeRef.current !== "browsing") return
            if (e.key === "ArrowLeft") {
                e.preventDefault()
                targetRef.current = Math.max(0, Math.round(positionRef.current) - 1)
            }
            if (e.key === "ArrowRight") {
                e.preventDefault()
                targetRef.current = Math.min(N - 1, Math.round(positionRef.current) + 1)
            }
            if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                openBook(modN(Math.round(positionRef.current), N))
            }
        }

        section.addEventListener("wheel", onWheel, { passive: false })
        section.addEventListener("pointerdown", onPointerDown)
        section.addEventListener("pointermove", onPointerMove)
        section.addEventListener("pointerup", onPointerUp)
        section.addEventListener("pointercancel", onPointerUp)
        window.addEventListener("keydown", onKey)

        return () => {
            section.removeEventListener("wheel", onWheel)
            section.removeEventListener("pointerdown", onPointerDown)
            section.removeEventListener("pointermove", onPointerMove)
            section.removeEventListener("pointerup", onPointerUp)
            section.removeEventListener("pointercancel", onPointerUp)
            window.removeEventListener("keydown", onKey)
            if (settleTimer) clearTimeout(settleTimer)
        }
    }, [openBook, closeBook])

    // ---- Buy panel reveal ----------------------------------------------------
    useEffect(() => {
        const panel = buyPanelRef.current
        if (!panel) return
        const content = panel.querySelector(".buy-panel-content")
        const children = content ? content.children : null

        if (showPanel) {
            gsap.killTweensOf(panel)
            if (children) gsap.killTweensOf(children)
            gsap.fromTo(panel, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.5, ease: "power2.out" })
            if (children) {
                gsap.fromTo(
                    children,
                    { autoAlpha: 0, x: -28, filter: "blur(5px)" },
                    {
                        autoAlpha: 1,
                        x: 0,
                        filter: "blur(0px)",
                        duration: 0.7,
                        ease: "power3.out",
                        stagger: { each: 0.06, from: "start" }
                    }
                )
            }
        } else {
            gsap.killTweensOf(panel)
            if (children) gsap.killTweensOf(children)
            gsap.to(panel, { autoAlpha: 0, duration: 0.35, ease: "power2.in" })
            if (children) {
                gsap.to(children, {
                    x: -20,
                    autoAlpha: 0,
                    filter: "blur(4px)",
                    duration: 0.3,
                    ease: "power2.in",
                    stagger: { each: 0.02, from: "end" }
                })
            }
        }
    }, [showPanel])

    // ---- Derived -------------------------------------------------------------
    const activeBook = BOOKS[activeIndex] ?? BOOKS[INITIAL_INDEX]
    const selectedBook = selectedIndex >= 0 ? BOOKS[selectedIndex] : null
    const accent = isDark ? "#d4a25a" : "#9a5e2c"
    const subtitleColor = isDark ? "rgba(245, 239, 226, 0.55)" : "rgba(26, 19, 16, 0.58)"

    const sectionStyle: CSSProperties & Record<string, string> = {
        backgroundColor: isDark ? "#1a130a" : "#f5efe2",
        color: isDark ? "#f3ecd8" : "#1a1310",
        "--hb-bg": isDark ? "#1a130a" : "#f5efe2",
        "--hb-fg": isDark ? "#f3ecd8" : "#1a1310",
        "--hb-accent": accent
    }

    return (
        <section
            ref={sectionRef}
            aria-label="Interactive 3D bookshelf of twelve entrepreneurship books"
            className="relative isolate h-screen w-full select-none overflow-hidden"
            style={sectionStyle}
        >
            {/* Background images — theme crossfade */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    backgroundImage: `url(${BG_IMAGE_DARK})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center bottom",
                    backgroundRepeat: "no-repeat",
                    opacity: isDark ? 1 : 0,
                    transition: "opacity 600ms ease"
                }}
            />
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    backgroundImage: `url(${BG_IMAGE_LIGHT})`,
                    backgroundSize: "cover",
                    backgroundPosition: "center bottom",
                    backgroundRepeat: "no-repeat",
                    opacity: isDark ? 0 : 1,
                    transition: "opacity 600ms ease"
                }}
            />

            {/* Vignette */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background:
                        "radial-gradient(ellipse 130% 130% at 50% 50%, rgba(0,0,0,0) 60%, rgba(0,0,0,0.32) 100%)"
                }}
            />

            {/* Ambient glow (GSAP pulse) */}
            <div
                ref={glowRef}
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                    background: `radial-gradient(circle at 50% 62%, ${
                        isDark ? "rgba(212,162,90,0.18)" : "rgba(154,94,44,0.10)"
                    }, rgba(0,0,0,0) 56%)`,
                    mixBlendMode: "screen"
                }}
            />

            {/* Film grain */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-[0.045] mix-blend-overlay"
                style={{ backgroundImage: GRAIN, backgroundSize: "160px 160px" }}
            />

            {/* WebGL scene */}
            <div className="absolute inset-0 z-[1]">
                <HardbackScene
                    isDark={isDark}
                    isMobile={isMobile}
                    onReady={startEntrance}
                    positionRef={positionRef}
                    targetRef={targetRef}
                    modeRef={modeRef}
                    selectedIndexRef={selectedIndexRef}
                    openProgressRef={openProgressRef}
                    enterProgressRef={enterProgressRef}
                    hoverIndexRef={hoverIndexRef}
                    onBookClick={handleBookClick}
                    onBookHover={handleBookHover}
                    onBookOut={handleBookOut}
                />
            </div>

            {/* Theme toggle */}
            <button
                type="button"
                onClick={toggleTheme}
                data-no-drag
                aria-label={isDark ? "Switch to daytime light" : "Switch to evening lamp"}
                className="absolute right-5 top-5 z-[4] inline-flex h-10 w-10 items-center justify-center rounded-full backdrop-blur-sm transition-opacity hover:opacity-100 md:right-7 md:top-7"
                style={{
                    color: "var(--hb-fg)",
                    border: "1px solid color-mix(in srgb, var(--hb-fg) 24%, transparent)",
                    backgroundColor: "color-mix(in srgb, var(--hb-bg) 55%, transparent)",
                    opacity: 0.78
                }}
            >
                {isDark ? <Sun className="size-[18px]" /> : <Moon className="size-[18px]" />}
            </button>

            {/* Top-centre chrome */}
            <div className="pointer-events-none absolute inset-x-0 top-0 z-[2] flex flex-col items-center px-6 pt-[12vh] text-center">
                <div
                    ref={titleRef}
                    className="font-medium leading-[0.96] tracking-[-0.02em]"
                    style={{
                        fontFamily: SERIF_VAR,
                        color: "var(--hb-fg)",
                        fontSize: "clamp(46px, 6.6vw, 108px)",
                        perspective: "1400px",
                        perspectiveOrigin: "50% 90%",
                        transformStyle: "preserve-3d"
                    }}
                >
                    {renderFlipTitle("The ", { italic: true, baseKey: "ti-1" })}
                    {renderFlipTitle("Founders", { italic: false, baseKey: "ti-2" })}
                    {renderFlipTitle(" Shelf", { italic: true, baseKey: "ti-3" })}
                </div>

                <div
                    ref={subtitleRef}
                    className="mt-5 max-w-[44rem] italic"
                    style={{
                        color: subtitleColor,
                        fontFamily: SERIF_VAR,
                        fontSize: "clamp(15px, 1.18vw, 19px)",
                        fontWeight: 400,
                        lineHeight: 1.6,
                        visibility: "hidden"
                    }}
                >
                    Twelve books on building, growing, and lasting. Pull one from the shelf; the lamp is on.
                </div>

                <div
                    ref={activeLineRef}
                    className="mt-8 flex flex-col items-center gap-1.5 text-[11.5px] font-medium uppercase tracking-[0.24em] md:flex-row md:gap-3"
                    style={{ color: "var(--hb-accent)", visibility: "hidden" }}
                >
                    <span className="hidden h-px w-10 bg-current opacity-50 md:block" />
                    <span>{activeBook.title}</span>
                    <span className="hidden opacity-55 md:inline">·</span>
                    <span className="text-[10.5px] opacity-75 md:text-[11.5px] md:opacity-100">
                        {activeBook.author}
                    </span>
                    <span className="hidden h-px w-10 bg-current opacity-50 md:block" />
                </div>
            </div>

            {/* Buy panel */}
            <aside
                ref={buyPanelRef}
                aria-label={selectedBook ? `${selectedBook.title} — details` : "Book details"}
                data-no-drag
                className="pointer-events-auto absolute bottom-[3vh] left-1/2 z-[3] w-[88vw] max-w-[480px] -translate-x-1/2 overflow-y-auto opacity-0 top-[45vh] md:bottom-auto md:left-[calc(50%+3.5vw)] md:top-1/2 md:w-[min(420px,37vw)] md:max-h-[78vh] md:max-w-none md:-translate-y-1/2 md:translate-x-0"
                style={{ color: "var(--hb-fg)", visibility: "hidden" }}
            >
                {selectedBook && (
                    <div className="buy-panel-content px-1 py-2">
                        <div className="flex items-start justify-between gap-4">
                            <div className="pt-1 text-[10px] uppercase tracking-[0.24em] opacity-55">
                                Hardback · {selectedBook.author.split(" ").pop()}
                            </div>
                            <button
                                type="button"
                                onClick={closeBook}
                                data-no-drag
                                aria-label="Close book"
                                className="inline-flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[10.5px] font-medium uppercase tracking-[0.22em] opacity-75 transition-opacity hover:opacity-100"
                                style={{
                                    color: "var(--hb-fg)",
                                    border: "1px solid color-mix(in srgb, var(--hb-fg) 30%, transparent)"
                                }}
                            >
                                <span>Close</span>
                                <span aria-hidden className="-mr-0.5 text-[15px] leading-none">
                                    ×
                                </span>
                            </button>
                        </div>

                        <h2
                            className="mt-3 font-medium leading-[1.05] tracking-[-0.012em]"
                            style={{ fontFamily: SERIF_VAR, fontSize: "clamp(28px, 2.4vw, 42px)" }}
                        >
                            {selectedBook.title}
                        </h2>

                        <div className="mt-1.5 text-[13px] tracking-[0.06em] opacity-70">
                            by {selectedBook.author}
                        </div>

                        <p
                            className="mt-6 max-w-[36ch] opacity-80"
                            style={{ fontFamily: SERIF_VAR, fontSize: "clamp(15px, 1.05vw, 17px)", lineHeight: 1.55 }}
                        >
                            {selectedBook.synopsis}
                        </p>

                        <blockquote
                            className="mt-6 pl-4 italic opacity-75"
                            style={{
                                fontFamily: SERIF_VAR,
                                fontSize: "clamp(13.5px, 0.95vw, 15.5px)",
                                lineHeight: 1.5,
                                borderLeft: "1px solid color-mix(in srgb, var(--hb-fg) 25%, transparent)"
                            }}
                        >
                            &ldquo;{selectedBook.excerpt}&rdquo;
                        </blockquote>

                        <button
                            type="button"
                            data-no-drag
                            onClick={() => {
                                const r = selectedBook.retailers[0]
                                if (r) window.open(r.url, "_blank", "noopener,noreferrer")
                            }}
                            aria-label={`Buy ${selectedBook.title}`}
                            className="group mt-8 flex w-fit cursor-pointer items-center justify-center gap-3 rounded-full px-7 py-3.5 text-[12px] font-medium uppercase tracking-[0.2em] hover:gap-4 [transition:gap_220ms_ease,opacity_220ms_ease,box-shadow_220ms_ease]"
                            style={{ backgroundColor: "var(--hb-fg)", color: "var(--hb-bg)" }}
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 16 16"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden
                            >
                                <path d="M2.5 4.5h11l-1 9.5H3.5l-1-9.5z" />
                                <path d="M5.5 4.5V3.2a2.5 2.5 0 015 0V4.5" />
                            </svg>
                            <span>Buy this book</span>
                            <svg
                                width="12"
                                height="12"
                                viewBox="0 0 14 14"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.6"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="transition-transform group-hover:translate-x-0.5"
                                aria-hidden
                            >
                                <path d="M2 7h10M8 3l4 4-4 4" />
                            </svg>
                        </button>

                        <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] opacity-70">
                            {selectedBook.retailers.map((r, idx) => (
                                <Fragment key={r.name}>
                                    {idx > 0 && (
                                        <span aria-hidden className="opacity-50">
                                            ·
                                        </span>
                                    )}
                                    <a
                                        href={r.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        data-no-drag
                                        className="underline-offset-4 transition-opacity hover:underline hover:opacity-100"
                                    >
                                        {r.name} <span className="opacity-70">{r.price}</span>
                                    </a>
                                </Fragment>
                            ))}
                        </div>

                        <div className="mt-5 hidden text-[10px] uppercase tracking-[0.24em] opacity-45 md:block">
                            press esc to close
                        </div>
                    </div>
                )}
            </aside>
        </section>
    )
}
